import React, {useEffect, useState} from 'react';
import AdminLayout from '../../components/layout/admin';
import Breadcrumbs from '../../components/elements/Breadcrumbs';
import {useQuery} from '@tanstack/react-query';
import {
    Flex,
    Text,
    Title,
    Card,
    Button,
    Stack
} from '@mantine/core';
import * as Unicons from '@iconscout/react-unicons';
import ModalConfirmation from '../../components/fragments/ModalConfirmation';
import LoadingIllustration from '../../components/fragments/IllustrationState/LoadingIllustration';
import Select from "react-select";
import {
    getAllEventIdAndNames,
    getEventEnrollmentCount, getSyncDetail,
    getSyncHistory,
    postEventSynchronization
} from "../../services/event";
import Swal from "sweetalert2";
import DataTables from "../../components/elements/DataTables";
import {showNotification} from "../../utilities/mantineNotification";
import ModalWrapper from "../../components/elements/ModalWrapper";
import LabelDate from "../../components/fragments/LabelDate";
import {renderDate} from "../../utilities/renderDate";
import {Input} from "reactstrap";
import {store} from "../../store/AppStore";

interface OptionType {
    label: string;
    value: string;
}

interface EventData {
    id: string;
    coursename: string;
}

interface SynchronizationDetail {
    errorMessage: string,
    stackTrace: string,
    courseName: string,
    totalEnrollmentBeelajar2: number,
    totalEnrollmentLms: number,
    totalUnenrollmentBeelajar2: number
}

interface SynchronizationReportProps extends SynchronizationDetail {
    closeModal?: () => void
}

interface SynchronizationHistoryDetail {
    id: string;
    eventName: string;
    userIn: string;
    dateIn: string;
}

interface EventListProps {
    selectedEvent?: EventData,
    setSelectedEvent?: (value: (((prevState: EventData) => EventData) | EventData)) => void,
    setIsModalVisible?: (value: (((prevState: boolean) => boolean) | boolean)) => void
}

interface ModalProps {
    isModalConfirmationVisible: boolean,
    selectedEvent: EventData,
    setIsModalConfirmationVisible?: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    synchronizationDetail?: SynchronizationDetail,
    setSynchronizationDetail?: (value: (((prevState: SynchronizationDetail) => SynchronizationDetail) | SynchronizationDetail)) => void,
}

interface EventSyncReportProps {
    setSynchronizationDetail?: (value: (((prevState: SynchronizationDetail) => SynchronizationDetail) | SynchronizationDetail)) => void,
    synchronizationDetail?: SynchronizationDetail,
}

const useEventIdAndNames = () => useQuery(
    {
        queryKey: ['events'],
        queryFn: () => getAllEventIdAndNames()
    }
);

const useEventEnrollmentCount = (eventId: string) => useQuery(
    {
        queryKey: ['event-enrollment-count', eventId],
        queryFn: () => getEventEnrollmentCount(eventId),
        enabled: !!eventId,
    }
);

const useSyncCourseHistory = () => useQuery(
    {
        queryKey: ['sync-course-history'],
        queryFn: () => getSyncHistory(),
    }
);

const useViewDetailSync = (syncId: string) => useQuery(
    {
        queryKey: ['sync-view-detail', syncId],
        queryFn: () => getSyncDetail(syncId),
        enabled: !!syncId,
    }
);

const EventList = (props: EventListProps) => {
    const [selectedOption, setSelectedOption] = useState<OptionType>(null);
    const eventIdAndNamesQuery = useEventIdAndNames();
    const eventEnrollmentCountQuery = useEventEnrollmentCount(props.selectedEvent?.id);

    const getOptions = (): Array<OptionType> => {
        const eventData: Array<EventData> = eventIdAndNamesQuery.data;
        const options: Array<OptionType> = [];
        if (!eventData){
            return options;
        }
        for (const event of eventData) {
            options.push({
                value: event.id,
                label: event.coursename
            })
        }
        return options
    }

    const getEnrollmentCount = (): { beelajar2: string, lms: string } => {
        if (props.selectedEvent == null) {
            const message = '-'
            return {
                beelajar2: message,
                lms: message
            }
        }

        if (eventEnrollmentCountQuery.isLoading) {
            const message = 'Loading...'
            return {
                beelajar2: message,
                lms: message
            }
        }

        if (eventEnrollmentCountQuery.isError) {
            const message = 'Failed to fetch data, please reselect again'
            return {
                beelajar2: message,
                lms: message
            }
        }

        return {
            beelajar2: eventEnrollmentCountQuery.data.beelajar2,
            lms: eventEnrollmentCountQuery.data.lms
        }
    }

    const updateSelectedEvent = (selectedOption: OptionType) => {
        setSelectedOption(selectedOption);
        props.setSelectedEvent({
            coursename: selectedOption.label,
            id: selectedOption.value
        });
    }

    const buttonSyncClicked = () => {
        if (!props.selectedEvent) {
            showNotification({
                type: 'error',
                title: 'Please select a course!',
                message: "You haven't select any course"
            })
            return;
        }
        props.setIsModalVisible(true);
    }

    if (eventIdAndNamesQuery.isLoading)
        return <Card>
            <LoadingIllustration
                description="Please wait... Currently collecting data..."
                title="Preparing Data"
            />
        </Card>

    if (eventIdAndNamesQuery.isError)
        Swal.fire({
            icon: 'error',
            title: 'Error fetching Event Data',
            text: `Error detail: ${eventIdAndNamesQuery.error}`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            confirmButtonText: "Reload Page"
        }).then(() => {
            window.location.reload();
        })

    return (
        <Card>
            <Stack gap="xs">
                <Title order={4}>Sync Course Enrollment</Title>
                <Select
                    name={'selectedEvent'}
                    options={getOptions()}
                    value={selectedOption}
                    onChange={updateSelectedEvent}
                    menuPosition={"fixed"}
                />
                <Text>
                                <span style={{
                                    display: 'inline-block',
                                    minWidth: "250px"
                                }}>Total participant on Beelajar LMS</span>
                    <span>: </span>
                    <span>{getEnrollmentCount().lms}</span>
                </Text>
                <Text>
                                <span style={{
                                    display: 'inline-block',
                                    minWidth: "250px"
                                }}>Total participant on Beelajar 2.0</span>
                    <span>: </span>
                    <span>{getEnrollmentCount().beelajar2}</span>
                </Text>
                <Flex justify={'center'}>
                    <Button
                        color="#7B2183"
                        radius="md"
                        onClick={buttonSyncClicked}
                        style={{
                            width: '200px',
                        }}
                    >
                        Sync Enrollment Data
                    </Button>
                </Flex>
            </Stack>
        </Card>)
}

const EventSyncReport = (props: EventSyncReportProps) => {
    const [selectedSyncItem, setSelectedSyncItem] = useState<SynchronizationHistoryDetail>(null);
    const viewDetailSyncQuery = useViewDetailSync(selectedSyncItem?.id);
    const syncCourseHistoryQuery = useSyncCourseHistory();
    const [filteredList, setFilteredList] = useState<SynchronizationHistoryDetail[]>();
    const renderDateElement = (item: any, key: string | number) => <LabelDate date={item[key]}/>

    const columns = [
        {
            heading: 'Course Name',
            props: 'eventName',
            id: 'eventName',
            sortable: true
        },
        {
            heading: 'User Created',
            props: 'userName',
            id: 'userName',
            sortable: true
        },
        {
            heading: 'Date Created',
            props: (item: any) => renderDateElement(item, 'dateIn'),
            id: 'dateIn',
            sortable: true
        },
        {
            heading: 'Action',
            props: 'action',
            id: 'action',
            sortable: true
        }
    ];

    const handleViewData = async (item: SynchronizationHistoryDetail) => {
        setSelectedSyncItem(item);
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const currentValue = event.target.value;
        const currentData: SynchronizationHistoryDetail[] = syncCourseHistoryQuery.data;

        const newFilteredList: SynchronizationHistoryDetail[] = currentData
            .filter(element =>
                element.eventName.toLowerCase().includes(currentValue.toLowerCase())
            );
        setFilteredList(newFilteredList);
    }

    const getTableData = () => {
        if(!!filteredList){
            return filteredList;
        }

        if(!!syncCourseHistoryQuery.data){
            return syncCourseHistoryQuery.data;
        }

        return []
    }

    useEffect(() => {
        if(viewDetailSyncQuery.isInitialLoading)
            Swal.fire({
                title: `Fetching data for ${selectedSyncItem?.eventName} | ${renderDate(selectedSyncItem?.dateIn, 'datetimeyear')}`,
                html: 'Please wait...',
                didOpen() {
                    Swal.showLoading()
                },
                allowOutsideClick: false,
                allowEscapeKey: false
            });

        if(!!viewDetailSyncQuery.data) {
            Swal.close()
            props.setSynchronizationDetail({
                courseName: viewDetailSyncQuery.data.eventName,
                errorMessage: null,
                stackTrace: null,
                totalEnrollmentBeelajar2: viewDetailSyncQuery.data.totalEnrollmentBeelajar2,
                totalEnrollmentLms: viewDetailSyncQuery.data.totalEnrollmentLms,
                totalUnenrollmentBeelajar2: viewDetailSyncQuery.data.totalUnenrollmentBeelajar2
            })
            setSelectedSyncItem(null);
        }
    }, [viewDetailSyncQuery.data, viewDetailSyncQuery.isInitialLoading])

    if (syncCourseHistoryQuery.isLoading)
        return <Card>
            <LoadingIllustration
                description="Please wait... Currently collecting data..."
                title="Preparing Data"
            />
        </Card>

    if (syncCourseHistoryQuery.isError)
        Swal.fire({
            icon: 'error',
            title: 'Error fetching History Data',
            text: `Error detail: ${syncCourseHistoryQuery.error}`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            confirmButtonText: "Reload Page"
        }).then(() => {
            window.location.reload();
        })

    return (<Card>
        <Flex
            style={{
                justifyContent: 'space-between',
                alignItems: 'center'
            }}
        >
            <Stack mb={10} gap={'xs'}>
                <Title order={4}>History Sync Course Enrollment</Title>
                <Text size="sm">Below is the list of history sync course enrollment.</Text>
            </Stack>
        </Flex>
        <Input
            name="event"
            bsSize="sm"
            placeholder="Search Course Name"
            type="text"
            onChange={handleInputChange}
        />
        <DataTables
            columns={columns}
            loading={false}
            data={getTableData()}
            handleView={handleViewData}
            viewText={"View Data"}
        />
    </Card>);
}

const SynchronizationReport = (props: SynchronizationReportProps) => {
    if (props.errorMessage) {
        return <Flex direction={"column"} gap={"md"}>
            <Stack gap={"xs"}>
                <Title order={5}>Error Message:</Title>
                <Text>{props.errorMessage}</Text>
            </Stack>
            <Stack gap={"xs"}>
                <Title order={5}>Stack Trace:</Title>
                <Text>{props.stackTrace}</Text>
            </Stack>
            <Button
                color="#7B2183"
                radius="md"
                onClick={props.closeModal}
                style={{
                    width: '100px',
                    alignSelf: 'center'
                }}
            >
                OK
            </Button>
        </Flex>
    }

    return <Flex direction={"column"} gap={"md"}>
        <Title order={5}>
            Course: <Text span style={{fontWeight: 'normal'}}>{props.courseName}</Text>
        </Title>
        <Stack gap={"xs"}>
            <Title order={5}>Total Enrollment to Beelajar LMS:</Title>
            <Text>{props.totalEnrollmentLms}</Text>
        </Stack>
        <Stack gap={"xs"}>
            <Title order={5}>Total Enrollment to Beelajar 2.0:</Title>
            <Text>{props.totalEnrollmentBeelajar2}</Text>
        </Stack>
        <Stack gap={"xs"}>
            <Title order={5}>Total Un-enrollment to Beelajar 2.0:</Title>
            <Text>{props.totalUnenrollmentBeelajar2}</Text>
        </Stack>
        <Button
            color="#7B2183"
            radius="md"
            onClick={props.closeModal}
            style={{
                width: '100px',
                alignSelf: 'center'
            }}
        >
            OK
        </Button>
    </Flex>
}

const SynchronizationSwal = (selectedEvent: EventData, setSynchronizationDetail: React.Dispatch<React.SetStateAction<SynchronizationDetail>>) =>
    Swal.fire({
        title: `Synchronizing ${selectedEvent.coursename}`,
        html: 'Please wait...',
        didOpen: async () => {
            Swal.showLoading();
            const reduxState: any = store.getState();
            let result: any;
            try {
                result = await postEventSynchronization({
                    eventId: selectedEvent.id,
                    user: reduxState.auth.userData.binusianId
                });
                Swal.close();
                setSynchronizationDetail({
                    ...result,
                    courseName: result.eventName,
                });
            } catch (error: any) {
                Swal.close();
                setSynchronizationDetail({
                    courseName: selectedEvent.coursename,
                    errorMessage: error.errorMessage,
                    stackTrace: error.stackTrace,
                    totalEnrollmentBeelajar2: 0,
                    totalEnrollmentLms: 0,
                    totalUnenrollmentBeelajar2: 0,
                });
            }
        },
        allowOutsideClick: false,
        allowEscapeKey: false
    });

const SyncCourseModal = (props: ModalProps) => {
    const syncCourseHistoryQuery = useSyncCourseHistory();
    const eventEnrollmentCountQuery = useEventEnrollmentCount(props.selectedEvent?.id);

    const confirmSync = async () => {
        props.setIsModalConfirmationVisible(false);
        await SynchronizationSwal(props.selectedEvent, props.setSynchronizationDetail);
        await eventEnrollmentCountQuery.refetch();
        await syncCourseHistoryQuery.refetch();
    }

    const closeModal = () => {
        props.setSynchronizationDetail(null)
    }

    return (<>
        <ModalConfirmation
            title={`Synchronize ${props.selectedEvent?.coursename}?`}
            description={`Are you sure you want to sync ${props.selectedEvent?.coursename} course?`}
            isVisible={props.isModalConfirmationVisible}
            onCancel={() => props.setIsModalConfirmationVisible(false)}
            onConfirm={confirmSync}
            submitText={'Synchronize'}
        />
        <ModalWrapper
            isVisible={!!props.synchronizationDetail}
            onClose={closeModal}
            title={props.synchronizationDetail?.errorMessage ? 'Uh Oh! An error occured!' : 'Synchronization Report'}>
            <SynchronizationReport
                closeModal={closeModal}
                {...props.synchronizationDetail}
            />
        </ModalWrapper>
    </>)
}

const SyncCourseEnrollment = () => {
    const [selectedEvent, setSelectedEvent] = useState<EventData>();
    const [isModalConfirmationVisible, setIsModalConfirmationVisible] = useState<boolean>(false);
    const [synchronizationDetail, setSynchronizationDetail] = useState<SynchronizationDetail>(null);

    return (
        <AdminLayout>
            <Breadcrumbs
                pageTitle="Sync Course Enrollment"
                itemList={[
                    {
                        title: 'BCL&D Back Office',
                        href: '#',
                        icon: <Unicons.UilFileCopyAlt/>
                    },
                    {
                        title: 'Sync Course Enrollment',
                        href: 'management/sync-course-enrollment'
                    }
                ]}
            />
            <Flex
                direction="column"
                gap="lg"
            >
                <EventList
                    selectedEvent={selectedEvent}
                    setSelectedEvent={setSelectedEvent}
                    setIsModalVisible={setIsModalConfirmationVisible}
                />
                <EventSyncReport
                    setSynchronizationDetail={setSynchronizationDetail}
                    synchronizationDetail={synchronizationDetail}
                />
            </Flex>
            <SyncCourseModal
                isModalConfirmationVisible={isModalConfirmationVisible}
                selectedEvent={selectedEvent}
                setIsModalConfirmationVisible={setIsModalConfirmationVisible}
                synchronizationDetail={synchronizationDetail}
                setSynchronizationDetail={setSynchronizationDetail}
            />
        </AdminLayout>
    );
};

export default SyncCourseEnrollment;
